import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "";

const DAY_NAMES  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmt12(time24) {
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function dateStr(year, month, day) {
  return `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
}

export default function AppointmentPage() {
  const [availability, setAvailability] = useState(null);
  const [viewYear,  setViewYear]  = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots,  setSlots]  = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", topic: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState("");
  const [step, setStep] = useState(1); // 1=calendar, 2=form

  useEffect(() => {
    fetch(`${API}/api/appointments/availability`)
      .then(r => r.json())
      .then(j => { if (j.success) setAvailability(j.data); })
      .catch(() => {});
  }, []);

  const loadSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedTime(null);
    try {
      const r = await fetch(`${API}/api/appointments/slots?date=${date}`);
      const j = await r.json();
      if (j.success) setSlots(j.slots);
    } catch (_) {}
    setLoadingSlots(false);
  }, []);

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    loadSlots(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/appointments/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate, time: selectedTime, ...form }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Booking failed.");
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const today = todayStr();

  const isDayOpen = (dayIndex) => {
    if (!availability) return true;
    return availability.days?.find(d => d.dayIndex === dayIndex)?.isOpen ?? false;
  };

  const isBlocked = (ds) => availability?.blockedDates?.includes(ds);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  if (success) {
    const [h, m] = (selectedTime || "00:00").split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const time12 = `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
    const dateDisplay = new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    return (
      <div style={{ minHeight: "100vh", background: "#f0f4fa", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
        <div style={{ background: "#fff", borderRadius: "20px", padding: "56px 48px", maxWidth: "520px", width: "100%", textAlign: "center", border: "1px solid rgba(1,46,114,0.1)", boxShadow: "0 8px 40px rgba(1,46,114,0.1)" }}>
          <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "10px" }}>Appointment Confirmed</p>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "22px", color: "#012E72", marginBottom: "16px" }}>You're all set!</h2>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "15px", color: "#4B5563", lineHeight: 1.7, marginBottom: "28px" }}>
            A confirmation has been sent to <strong>{form.email}</strong>.<br/>
            We look forward to speaking with you.
          </p>
          <div style={{ background: "#f7f9ff", border: "1px solid #e8eef8", borderRadius: "12px", padding: "20px 24px", marginBottom: "32px", textAlign: "left" }}>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "12px" }}>Appointment Details</p>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "15px", color: "#012E72", margin: "0 0 4px" }}>{dateDisplay}</p>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "15px", color: "#012E72", margin: 0 }}>{time12}</p>
          </div>
          <a href="/" style={{ display: "inline-block", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", borderRadius: "999px", padding: "14px 32px", textDecoration: "none" }}>Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4fa", padding: "60px 20px 80px" }}>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "12px" }}>
            Schedule a Meeting
          </p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "32px", color: "#012E72", marginBottom: "14px" }}>Book an Appointment</h1>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "16px", color: "#6B7280", maxWidth: "520px", margin: "0 auto", lineHeight: 1.7 }}>
            Choose a date and time that works for you. One of our licensed agents will call you at your appointment time.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: step === 1 ? "1fr 340px" : "1fr", gap: "24px", alignItems: "start" }}>

          {/* Calendar + Slots */}
          {step === 1 && (
            <>
              {/* Calendar */}
              <div style={{ background: "#fff", borderRadius: "20px", padding: "32px", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 20px rgba(1,46,114,0.07)" }}>
                {/* Month nav */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
                  <button onClick={prevMonth} style={{ background: "none", border: "1px solid rgba(1,46,114,0.12)", borderRadius: "8px", width: "36px", height: "36px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#012E72" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <p style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "15px", color: "#012E72" }}>
                    {MONTH_NAMES[viewMonth]} {viewYear}
                  </p>
                  <button onClick={nextMonth} style={{ background: "none", border: "1px solid rgba(1,46,114,0.12)", borderRadius: "8px", width: "36px", height: "36px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#012E72" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>

                {/* Day labels */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "8px" }}>
                  {DAY_NAMES.map(d => (
                    <div key={d} style={{ textAlign: "center", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", padding: "4px 0" }}>{d}</div>
                  ))}
                </div>

                {/* Day grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
                  {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const ds = dateStr(viewYear, viewMonth, day);
                    const dayIndex = new Date(ds + "T12:00:00").getDay();
                    const isPast    = ds < today;
                    const isWeekend = !isDayOpen(dayIndex);
                    const blocked   = isBlocked(ds);
                    const disabled  = isPast || isWeekend || blocked;
                    const isSelected = ds === selectedDate;
                    const isToday   = ds === today;
                    return (
                      <button
                        key={day}
                        onClick={() => !disabled && handleSelectDate(ds)}
                        disabled={disabled}
                        style={{
                          aspectRatio: "1", borderRadius: "10px", border: "none",
                          fontFamily: "'Cinzel', serif", fontSize: "13px", fontWeight: isToday ? 700 : 400,
                          cursor: disabled ? "not-allowed" : "pointer",
                          transition: "all 0.15s",
                          background: isSelected ? "linear-gradient(135deg, #012E72, #002DB5)" : isToday ? "rgba(1,46,114,0.07)" : "transparent",
                          color: isSelected ? "#fff" : disabled ? "rgba(1,46,114,0.2)" : "#012E72",
                          outline: isToday && !isSelected ? "2px solid rgba(1,46,114,0.2)" : "none",
                          outlineOffset: "2px",
                        }}
                        onMouseEnter={e => { if (!disabled && !isSelected) e.currentTarget.style.background = "rgba(1,46,114,0.08)"; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? "rgba(1,46,114,0.07)" : "transparent"; }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.35)", marginTop: "20px", fontStyle: "italic" }}>
                  Available: Monday – Friday · 9 AM – 5 PM
                </p>
              </div>

              {/* Time slots */}
              <div style={{ background: "#fff", borderRadius: "20px", padding: "28px", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 20px rgba(1,46,114,0.07)" }}>
                {!selectedDate ? (
                  <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "rgba(1,46,114,0.06)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </div>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)" }}>Select a date to see available times</p>
                  </div>
                ) : loadingSlots ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                    <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                  </div>
                ) : (
                  <>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "16px" }}>
                      {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                    </p>
                    {slots.length === 0 ? (
                      <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(1,46,114,0.4)", fontStyle: "italic", textAlign: "center", padding: "24px 0" }}>
                        No available slots for this date. Please try another day.
                      </p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {slots.map(slot => (
                          <button
                            key={slot}
                            onClick={() => setSelectedTime(slot)}
                            style={{
                              padding: "12px 16px", borderRadius: "10px", textAlign: "left",
                              fontFamily: "'Constantia', serif", fontSize: "14px",
                              cursor: "pointer", transition: "all 0.15s",
                              background: selectedTime === slot ? "linear-gradient(135deg, #012E72, #002DB5)" : "rgba(1,46,114,0.03)",
                              color: selectedTime === slot ? "#fff" : "#012E72",
                              border: `1px solid ${selectedTime === slot ? "transparent" : "rgba(1,46,114,0.1)"}`,
                            }}
                            onMouseEnter={e => { if (selectedTime !== slot) e.currentTarget.style.background = "rgba(1,46,114,0.07)"; }}
                            onMouseLeave={e => { if (selectedTime !== slot) e.currentTarget.style.background = "rgba(1,46,114,0.03)"; }}
                          >
                            {fmt12(slot)}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedTime && (
                      <button
                        onClick={() => setStep(2)}
                        style={{ marginTop: "20px", width: "100%", padding: "13px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "12px", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,45,181,0.3)" }}
                      >
                        Continue →
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Booking form */}
          {step === 2 && (
            <div style={{ background: "#fff", borderRadius: "20px", padding: "40px", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 20px rgba(1,46,114,0.07)", maxWidth: "640px", margin: "0 auto", width: "100%" }}>
              {/* Selected slot summary */}
              <div style={{ background: "#f7f9ff", border: "1px solid #e8eef8", borderRadius: "12px", padding: "16px 20px", marginBottom: "32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)", marginBottom: "4px" }}>Selected</p>
                  <p style={{ fontFamily: "'Constantia', serif", fontSize: "15px", color: "#012E72", margin: 0 }}>
                    {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {fmt12(selectedTime)}
                  </p>
                </div>
                <button onClick={() => setStep(1)} style={{ background: "none", border: "none", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(1,46,114,0.5)", cursor: "pointer", textDecoration: "underline" }}>Change</button>
              </div>

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {[
                  { label: "Full Name *", key: "fullName", type: "text", required: true, placeholder: "Your full name" },
                  { label: "Email Address *", key: "email", type: "email", required: true, placeholder: "your@email.com" },
                  { label: "Phone Number", key: "phone", type: "tel", required: false, placeholder: "(805) 555-0000" },
                  { label: "Topic / Reason for Call", key: "topic", type: "text", required: false, placeholder: "e.g. Auto insurance quote, policy question…" },
                ].map(({ label, key, type, required, placeholder }) => (
                  <div key={key}>
                    <label style={{ display: "block", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,46,114,0.5)", marginBottom: "8px" }}>{label}</label>
                    <input
                      type={type} required={required} placeholder={placeholder}
                      value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: "100%", boxSizing: "border-box", padding: "12px 16px", fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "10px", background: "#fff", outline: "none" }}
                      onFocus={e => e.target.style.borderColor = "#002DB5"}
                      onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.15)"}
                    />
                  </div>
                ))}

                <div>
                  <label style={{ display: "block", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,46,114,0.5)", marginBottom: "8px" }}>Additional Notes</label>
                  <textarea
                    rows={3} placeholder="Anything else you'd like us to know…"
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    style={{ width: "100%", boxSizing: "border-box", padding: "12px 16px", fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72", border: "1px solid rgba(1,46,114,0.15)", borderRadius: "10px", background: "#fff", outline: "none", resize: "vertical" }}
                    onFocus={e => e.target.style.borderColor = "#002DB5"}
                    onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.15)"}
                  />
                </div>

                {error && (
                  <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#DC2626", background: "#FEF2F2", padding: "12px 16px", borderRadius: "8px", margin: 0 }}>{error}</p>
                )}

                <div style={{ display: "flex", gap: "12px" }}>
                  <button type="button" onClick={() => setStep(1)} style={{ flex: "0 0 auto", padding: "13px 24px", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", background: "rgba(1,46,114,0.05)", border: "1px solid rgba(1,46,114,0.12)", borderRadius: "12px", cursor: "pointer" }}>
                    Back
                  </button>
                  <button type="submit" disabled={submitting} style={{ flex: 1, padding: "13px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "12px", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1, boxShadow: "0 4px 16px rgba(0,45,181,0.3)" }}>
                    {submitting ? "Confirming…" : "Confirm Appointment"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
